import {
  type EpocusExam,
  RXDB_EPOCUS_EXAM_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-exam";
import type { ExamType } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import {
  type EpocusReview,
  RXDB_EPOCUS_REVIEW_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-review";
import { useCallback, useMemo } from "react";
import type { RxCollection } from "rxdb";
import { useRxData } from "rxdb-hooks";

export type ScoreBucket = {
  /** Bucket midpoint, 1.0 … 5.0 in 0.5 steps */
  score: number;
  count: number;
};

export type ExamTypeReviewCount = {
  examType: ExamType;
  count: number;
};

export type ReviewerStats = {
  /** Distinct exams reviewed — a resubmitted review does not double-count */
  scansReviewed: number;
  /** Avg qualityScore awarded, 1 d.p., or "—" when no reviews */
  averageScoreGiven: string;
  /** Share of reviews marked "agree", 0–100, or null when no reviews */
  agreementRate: number | null;
  /** Share of reviews carrying a non-empty comment, 0–100, or null when none */
  commentRate: number | null;
  /** Last ≤12 scores awarded, chronological */
  scoreTrend: number[];
  /** All 9 score buckets, always present so the chart keeps a stable x-axis */
  scoreDistribution: ScoreBucket[];
  /** Reviews submitted per week for the last 8 weeks */
  volumeTrend: number[];
  topFeedbackTags: Array<{ tag: string; pct: number }>;
  /** Exam types this reviewer has reviewed, busiest first */
  byExamType: ExamTypeReviewCount[];
};

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const SCORE_BUCKETS = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];

const computeReviewVolumeTrend = (
  reviews: EpocusReview[],
  now: number,
): number[] =>
  Array.from({ length: 8 }, (_, i) => {
    const weekStart = now - (8 - i) * WEEK_MS;
    const weekEnd = now - (7 - i) * WEEK_MS;
    return reviews.filter((r) => {
      const t = new Date(r.meta.createdAt).getTime();
      return t >= weekStart && t < weekEnd;
    }).length;
  });

const toPct = (count: number, total: number): number | null =>
  total > 0 ? Math.round((count / total) * 100) : null;

export const useReviewerStats = (reviewerId: string): ReviewerStats => {
  const reviewsQuery = useCallback(
    (collection: RxCollection<EpocusReview>) =>
      collection.find({ selector: { reviewerId: { $eq: reviewerId } } }),
    [reviewerId],
  );
  const { result: reviews } = useRxData<EpocusReview>(
    RXDB_EPOCUS_REVIEW_COLLECTION_NAME,
    reviewsQuery,
  );

  const reviewedExamIds = useMemo(
    () => reviews.map((review) => review.examId),
    [reviews],
  );

  const examsQuery = useCallback(
    (collection: RxCollection<EpocusExam>) =>
      reviewedExamIds.length > 0
        ? collection.find({ selector: { id: { $in: reviewedExamIds } } })
        : collection.find({ selector: { id: { $eq: "__none__" } } }),
    [reviewedExamIds],
  );
  const { result: exams } = useRxData<EpocusExam>(
    RXDB_EPOCUS_EXAM_COLLECTION_NAME,
    examsQuery,
  );

  return useMemo(() => {
    const now = Date.now();

    const myReviews = reviews;

    const reviewsSorted = [...myReviews].sort(
      (a, b) =>
        new Date(a.meta.createdAt).getTime() -
        new Date(b.meta.createdAt).getTime(),
    );

    const scansReviewed = new Set(myReviews.map((r) => r.examId)).size;

    const averageScoreGiven =
      myReviews.length > 0
        ? (
            myReviews.reduce((sum, r) => sum + r.qualityScore, 0) /
            myReviews.length
          ).toFixed(1)
        : "—";

    const agreementRate = toPct(
      myReviews.filter((r) => r.agreement === "agree").length,
      myReviews.length,
    );

    const commentRate = toPct(
      myReviews.filter((r) => r.comment != null && r.comment.trim() !== "")
        .length,
      myReviews.length,
    );

    const scoreTrend = reviewsSorted.slice(-12).map((r) => r.qualityScore);

    const countsByScore = new Map<number, number>();
    for (const review of myReviews) {
      countsByScore.set(
        review.qualityScore,
        (countsByScore.get(review.qualityScore) ?? 0) + 1,
      );
    }
    const scoreDistribution = SCORE_BUCKETS.map((score) => ({
      score,
      count: countsByScore.get(score) ?? 0,
    }));

    const volumeTrend = computeReviewVolumeTrend(myReviews, now);

    const tagCounts = new Map<string, number>();
    for (const review of myReviews) {
      for (const tag of review.feedbackTags) {
        tagCounts.set(tag, (tagCounts.get(tag) ?? 0) + 1);
      }
    }
    const topFeedbackTags = [...tagCounts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([tag, count]) => ({
        tag,
        pct: toPct(count, myReviews.length) ?? 0,
      }));

    const examTypeById = new Map<string, ExamType>(
      exams.map((e) => [e.id, e.examType]),
    );
    const countsByExamType = new Map<ExamType, number>();
    for (const review of myReviews) {
      const examType = examTypeById.get(review.examId);
      if (!examType) continue;
      countsByExamType.set(examType, (countsByExamType.get(examType) ?? 0) + 1);
    }
    const byExamType = [...countsByExamType.entries()]
      .map(([examType, count]) => ({ examType, count }))
      .sort((a, b) => b.count - a.count);

    return {
      scansReviewed,
      averageScoreGiven,
      agreementRate,
      commentRate,
      scoreTrend,
      scoreDistribution,
      volumeTrend,
      topFeedbackTags,
      byExamType,
    };
  }, [exams, reviews]);
};
